import axios from '@/plugins/axios';

const CACHE_DURATION = 5 * 60 * 1000;

export default {
  async fetchDashboardData({ commit, state }, { forceRefresh = false } = {}) {
    if (state.loadingDashboard) return;

    const now = new Date().getTime();
    const isDataValid = state.lastUpdate && now - state.lastUpdate < CACHE_DURATION;

    if (isDataValid && !forceRefresh) return;

    try {
      commit('SET_LOADING_DASHBOARD', true);

      const startOfMonth = new Date(now);
      startOfMonth.setDate(1);
      const endOfMonth = new Date(
        startOfMonth.getFullYear(),
        startOfMonth.getMonth() + 1,
        0,
      );

      const { data } = await axios.get(
        `${process.env.VUE_APP_STAGING}/v2/corporate/dashboard`,
        {
          params: {
            startDate: startOfMonth.toISOString().split('T')[0],
            endDate: endOfMonth.toISOString().split('T')[0],
          },
        },
      );
      if (data && data.success) {
        commit('SET_DASHBOARD_DATA', data.data);
      } else {
        commit('SET_DASHBOARD_DATA', null);
      }
    } catch (error) {
      console.error('fetchDashboardData error:', error);
      // Limpia el storage a través de la mutación segura
      try {
        commit('SET_DASHBOARD_DATA', null);
      } catch (_) {}
      throw error;
    } finally {
      commit('SET_LOADING_DASHBOARD', false);
    }
  },

  async fetchUsers({ commit }) {
    try {
      commit('SET_LOADING_USERS', true);
      const {
        data: { data },
      } = await axios.get(`${process.env.VUE_APP_STAGING}/v2/corporate/users`, {
        params: { isActive: true },
      });
      commit('SET_TOTAL_COLLABORATORS', data.users.length);
    } catch (error) {
      console.error(error);
      commit('SET_TOTAL_COLLABORATORS', 0);
    } finally {
      commit('SET_LOADING_USERS', false);
    }
  },

  async fetchAssociatedPanels({ commit }) {
    try {
      commit('SET_LOADING_ASSOCIATED_PANELS', true);

      const { data } = await axios.get(
        `${process.env.VUE_APP_STAGING}/v2/corporate/master/associated-panels`,
      );

      if (data && data.success) {
        const panels = Array.isArray(data?.data?.panels) ? data.data.panels : [];
        const parsedPanels = panels.map((panel) => ({
          ...panel,
          image_url: panel.image_url ? `${process.env.VUE_APP_IMAGE_BUCKET}${panel.image_url}` : '',
        }));

        commit('SET_ASSOCIATED_PANELS', parsedPanels);
      } else {
        commit('SET_ASSOCIATED_PANELS', []);
      }
    } catch (error) {
      console.error('fetchAssociatedPanels - Error:', error);
      commit('SET_ASSOCIATED_PANELS', []);
    } finally {
      commit('SET_LOADING_ASSOCIATED_PANELS', false);
    }
  },

  async fetchCreditDistribution(
    { commit, state },
    { forceRefresh = false } = {},
  ) {
    if (state.loadingCreditDistribution && !forceRefresh) return;

    const now = new Date().getTime();
    const hasFetchedCreditDistribution = state.creditDistributionLastUpdate !== null;
    const isDataValid = state.creditDistributionLastUpdate
      && now - Number(state.creditDistributionLastUpdate) < CACHE_DURATION;

    if (isDataValid && !forceRefresh && hasFetchedCreditDistribution) return;

    try {
      commit('SET_LOADING_CREDIT_DISTRIBUTION', true);

      const { data } = await axios.get(
        `${process.env.VUE_APP_STAGING}/v2/corporate/master/credit-distribution`,
      );

      const payload = data?.data || {};

      if (data && data.success) {
        commit('SET_CREDIT_DISTRIBUTION', {
          master_credit_limit: Number(payload.master_credit_limit) || 0,
          panels: (Array.isArray(payload.panels) ? payload.panels : []).map(
            (panel) => ({
              id: panel.id,
              name: panel.name,
              overdraft: Number(panel.overdraft) || 0,
            }),
          ),
        });
        commit('SET_CREDIT_DISTRIBUTION_LAST_UPDATE', now);
      } else {
        commit('SET_CREDIT_DISTRIBUTION', {
          master_credit_limit: 0,
          panels: [],
        });
        commit('SET_CREDIT_DISTRIBUTION_LAST_UPDATE', null);
      }
    } catch (error) {
      console.error('fetchCreditDistribution - Error:', error);
      commit('SET_CREDIT_DISTRIBUTION', {
        master_credit_limit: 0,
        panels: [],
      });
      commit('SET_CREDIT_DISTRIBUTION_LAST_UPDATE', null);
    } finally {
      commit('SET_LOADING_CREDIT_DISTRIBUTION', false);
    }
  },
};
